import { ReactNode } from 'react';
import { CallIntelTypes } from '@frontend/api-call-intel';

type FormSchedulingOutcome = CallIntelTypes.SchedulingOutcomeEnum | 'none';

export const DEFAULT_FIELD_EMPTY_VALUE = '-';

export type CorrectionBannerConfig = {
  canMakeCorrection: boolean;
  isReviewed: boolean;
  showEditHistory: boolean;
};

export type Suggestion = {
  title: string;
  description: string;
};

type FieldMappingValue = {
  emptyValue: string;
  dependentAnalysis: CallIntelTypes.AnalysisTypeEnum[];
  label: string;
  optionType: OptionType;
  type: keyof CallIntelTypes.CallEditableFields;
  suggestion?: Suggestion;
};

export type FieldMapping = Partial<Record<CallIntelTypes.AnalysisTypeEnum, FieldMappingValue>>;

export type OptionType =
  | 'appointmentTypes'
  | 'callDirection'
  | 'categories'
  | 'contactType'
  | 'feedbackIssueTypes'
  | 'schedulingOutcome'
  | 'schedulingOutcomeV2'
  | 'schedulingOpportunity'
  | 'sentiment'
  | 'taskStatus'
  | 'serviceQualityFlag';

export type RecordOptions = Record<string, ReactNode> | Record<string, string>;

export type BadgeOption = {
  chip: React.ReactNode;
  label?: string;
  submenuOptions?: Record<string, BadgeOption>;
};

export type FormState = {
  appointmentTypes: CallIntelTypes.AppointmentTypeEnum[];
  categories: CallIntelTypes.CategoryEnum[];
  contactType?: CallIntelTypes.ContactTypeEnum;
  schedulingOpportunity?: CallIntelTypes.SchedulingOpportunityEnum;
  schedulingOutcome?: FormSchedulingOutcome;
  schedulingOutcomeV2?: CallIntelTypes.SchedulingOutcomeV2Enum;
  callSentiment?: CallIntelTypes.SentimentEnum;
  serviceQualityFlags: CallIntelTypes.ServiceQualityFlagEnum[];
};

export const getFormStateByCall = (call?: CallIntelTypes.Call): FormState => ({
  appointmentTypes: call?.appointmentTypes ?? [],
  categories: call?.categories ?? [],
  contactType: call?.contactType,
  schedulingOpportunity:
    call?.callSchedulingOpportunity === null
      ? undefined
      : call?.callSchedulingOpportunity
      ? CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED
      : CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED,
  schedulingOutcome:
    call?.callSchedulingOutcome === null
      ? undefined
      : call?.callSchedulingOpportunity
      ? call?.callSchedulingOutcome
        ? CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED
        : CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED
      : 'none',
  schedulingOutcomeV2: call?.schedulingOutcomeV2,
  callSentiment: call?.callSentiment?.sentiment,
  serviceQualityFlags: call?.serviceQualityFlags ?? [],
});

/**
 * Transforms RecordOptions into a flat Record<string, BadgeOption>.
 */
export const transformOptionsToBadgeOptions = (
  options: RecordOptions,
  customLabels?: Record<string, string>
): Record<string, BadgeOption> => {
  return Object.entries(options).reduce<Record<string, BadgeOption>>((acc, [key, value]) => {
    acc[key] = {
      chip: value,
      label: customLabels?.[key] ?? '',
    };
    return acc;
  }, {});
};

/**
 * Transforms RecordOptions with nested submenus into Record<string, BadgeOption>.
 */
export const transformOptionsToNestedBadgeOptions = (
  optionsWithSubmenus: RecordOptions,
  suboptionsMap: Record<string, RecordOptions>,
  customLabels?: Record<string, string>
): Record<string, BadgeOption> => {
  return Object.entries(optionsWithSubmenus).reduce<Record<string, BadgeOption>>((acc, [key, value]) => {
    acc[key] = {
      chip: value,
      label: customLabels?.[key] ?? '',
      submenuOptions: suboptionsMap[key]
        ? Object.entries(suboptionsMap[key]).reduce<Record<string, BadgeOption>>((submenuAcc, [subKey, subValue]) => {
            submenuAcc[subKey] = {
              chip: subValue,
              label: customLabels?.[subKey] ?? '',
            };
            return submenuAcc;
          }, {})
        : undefined,
    };
    return acc;
  }, {});
};

/**
 * Replaces all empty string ("") values in an object with `undefined`.
 * Useful when working with forms where empty string fields should be treated as undefined.
 */

export const normalizeEmptyStrings = <T extends Record<string, any>>(obj: T): T => {
  return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, value === '' ? undefined : value])) as T;
};

export const mapFormStateToCall = (form: FormState): Partial<CallIntelTypes.Call> => {
  return {
    appointmentTypes: form.appointmentTypes.length > 0 ? form.appointmentTypes : undefined,
    categories: form.categories.length > 0 ? form.categories : undefined,
    contactType: form.contactType === undefined ? undefined : form.contactType,
    callSchedulingOpportunity:
      form.schedulingOpportunity === undefined
        ? null
        : form.schedulingOpportunity === CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED,
    callSchedulingOutcome:
      form.schedulingOutcome === undefined
        ? null
        : form.schedulingOutcome === CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED,
    callSentiment: form.callSentiment === undefined ? null : { sentiment: form.callSentiment },
    serviceQualityFlags: form.serviceQualityFlags.length > 0 ? form.serviceQualityFlags : undefined,
    schedulingOutcomeV2: form.schedulingOutcomeV2 === undefined ? undefined : form.schedulingOutcomeV2,
  };
};

export const computeIsFieldEdited = (
  currentEdited: CallIntelTypes.IsFieldEdited,
  changedFields: (keyof CallIntelTypes.CallEditableFields)[]
) => {
  const updatedEdited: CallIntelTypes.IsFieldEdited = { ...currentEdited };
  changedFields.forEach((field) => {
    const editedKey = fieldToEditedKeyMap[field];
    if (editedKey) {
      updatedEdited[editedKey] = true;
    }
  });
  return updatedEdited;
};

export const fieldToEditedKeyMap: Record<keyof CallIntelTypes.CallEditableFields, keyof CallIntelTypes.IsFieldEdited> =
  {
    appointmentTypes: 'isAppointmentTypeEdited',
    categories: 'isCategoryEdited',
    contactType: 'isContactTypeEdited',
    schedulingOpportunity: 'isSchedulingOpportunityEdited',
    schedulingOutcome: 'isSchedulingOutcomeEdited',
    callSentiment: 'isSentimentEdited',
    serviceQualityFlags: 'isServiceQualityFlagEdited',
    schedulingOutcomeV2: 'isSchedulingOutcomeEdited',
  };
